import type {
    AgentTaskCandidateOutcome,
    AgentTaskClarificationQuestion,
    AgentTaskProposedChangeResult,
    AgentTaskQuestionSourceLocation,
    AgentTaskTaskListResult,
    AgentTaskTaskSummary,
    AgentTaskWorkItemDetailResult,
} from './types';
import { AgentTaskProposalVote } from './types';

type ApiRecord = Record<string, unknown>;

function asRecord(value: unknown): ApiRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : {};
}

function first(record: ApiRecord, ...keys: string[]) {
    for (const key of keys) {
        if (record[key] !== undefined && record[key] !== null) return record[key];
    }
    return undefined;
}

function optionalString(value: unknown) {
    return typeof value === 'string' && value.trim() ? value : undefined;
}

function number(value: unknown, fallback = 0) {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function optionalNumber(value: unknown) {
    const parsed = number(value, NaN);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function nullableNumber(value: unknown) {
    return value === undefined || value === null ? null : optionalNumber(value) ?? null;
}

function boolean(value: unknown) {
    return value === true || value === 1 || value === 'true';
}

function normalizeVote(value: unknown): AgentTaskProposalVote | null {
    const parsed = nullableNumber(value);
    return parsed !== null && parsed >= AgentTaskProposalVote.Ok && parsed <= AgentTaskProposalVote.Deferred
        ? parsed as AgentTaskProposalVote
        : null;
}

function normalizeOutcome(value: unknown): AgentTaskCandidateOutcome {
    if (typeof value === 'string') {
        const normalized = value.toLowerCase();
        if (['proposed', 'retained', 'revised', 'discarded'].includes(normalized)) {
            return normalized as AgentTaskCandidateOutcome;
        }
    }
    return ['proposed', 'retained', 'revised', 'discarded'][number(value)] as AgentTaskCandidateOutcome
        ?? 'proposed';
}

function normalizeLocation(value: unknown): AgentTaskQuestionSourceLocation | undefined {
    const record = asRecord(value);
    const location: AgentTaskQuestionSourceLocation = {
        repository: optionalString(first(record, 'repository', 'Repository')),
        project: optionalString(first(record, 'project', 'Project')),
        path: optionalString(first(record, 'path', 'Path')),
        ref: optionalString(first(record, 'ref', 'Ref')),
        startLine: optionalNumber(first(record, 'startLine', 'StartLine')),
        endLine: optionalNumber(first(record, 'endLine', 'EndLine')),
        symbol: optionalString(first(record, 'symbol', 'Symbol')),
        snippet: optionalString(first(record, 'snippet', 'Snippet')),
        codeUrl: optionalString(first(record, 'codeUrl', 'CodeUrl')),
    };
    return Object.values(location).some(item => item !== undefined) ? location : undefined;
}

export function normalizeAgentTaskProposedChange(value: unknown): AgentTaskProposedChangeResult {
    const record = asRecord(value);
    const rawLocations = first(record, 'locations', 'Locations');
    return {
        analysisItemId: number(first(record, 'analysisItemId', 'AnalysisItemId')),
        candidateId: optionalString(first(record, 'candidateId', 'CandidateId')) ?? '',
        outcome: normalizeOutcome(first(record, 'outcome', 'Outcome')),
        summary: optionalString(first(record, 'summary', 'Summary')) ?? '',
        text: optionalString(first(record, 'text', 'Text')) ?? '',
        rationale: optionalString(first(record, 'rationale', 'Rationale')),
        expectedReductionPercent: optionalNumber(first(record, 'expectedReductionPercent', 'ExpectedReductionPercent')),
        analysisRound: number(first(record, 'analysisRound', 'AnalysisRound')),
        ordinal: number(first(record, 'ordinal', 'Ordinal')),
        originWorkItemId: number(first(record, 'originWorkItemId', 'OriginWorkItemId')),
        vote: normalizeVote(first(record, 'vote', 'Vote')),
        proposalPrId: nullableNumber(first(record, 'proposalPrId', 'ProposalPrId', 'ProposalPRId')),
        rowVersion: optionalString(first(record, 'rowVersion', 'RowVersion')) ?? '',
        isCurrent: boolean(first(record, 'isCurrent', 'IsCurrent')),
        locations: (Array.isArray(rawLocations) ? rawLocations : [])
            .map(normalizeLocation)
            .filter((location): location is AgentTaskQuestionSourceLocation => !!location),
    };
}

export function normalizeAgentTaskTaskSummary(value: unknown): AgentTaskTaskSummary {
    const record = asRecord(value);
    const rawPullRequests = first(record, 'pullRequests', 'PullRequests');
    const rawWorkItems = first(record, 'workItems', 'WorkItems');
    return {
        ...(record as unknown as AgentTaskTaskSummary),
        taskId: number(first(record, 'taskId', 'TaskId')),
        scope: optionalString(first(record, 'scope', 'Scope')) ?? '',
        deleted: boolean(first(record, 'deleted', 'Deleted')),
        flags: number(first(record, 'flags', 'Flags')),
        createdDate: optionalString(first(record, 'createdDate', 'CreatedDate')) ?? '',
        inclusive: number(first(record, 'inclusive', 'Inclusive')),
        exclusive: number(first(record, 'exclusive', 'Exclusive')),
        methodCount: number(first(record, 'methodCount', 'MethodCount')),
        subTaskCount: number(first(record, 'subTaskCount', 'SubTaskCount')),
        depth: number(first(record, 'depth', 'Depth')),
        activeProposalCount: number(first(record, 'activeProposalCount', 'ActiveProposalCount')),
        votedProposalCount: number(first(record, 'votedProposalCount', 'VotedProposalCount')),
        okProposalCount: number(first(record, 'okProposalCount', 'OkProposalCount')),
        hasStructuredAnalysis: boolean(first(record, 'hasStructuredAnalysis', 'HasStructuredAnalysis')),
        pullRequests: Array.isArray(rawPullRequests)
            ? rawPullRequests as AgentTaskTaskSummary['pullRequests']
            : [],
        workItems: Array.isArray(rawWorkItems)
            ? rawWorkItems as AgentTaskTaskSummary['workItems']
            : [],
    };
}

export function normalizeAgentTaskTaskListResult(value: unknown): AgentTaskTaskListResult {
    const record = asRecord(value);
    const rawTasks = first(record, 'tasks', 'Tasks');
    const tasks = (Array.isArray(rawTasks) ? rawTasks : []).map(normalizeAgentTaskTaskSummary);
    return {
        isSynthetic: boolean(first(record, 'isSynthetic', 'IsSynthetic')),
        taskListId: number(first(record, 'taskListId', 'TaskListId')),
        snapshotId: nullableNumber(first(record, 'snapshotId', 'SnapshotId')),
        snapshotName: optionalString(first(record, 'snapshotName', 'SnapshotName')),
        totalCount: number(first(record, 'totalCount', 'TotalCount'), tasks.length),
        tasks,
    };
}

export function normalizeAgentTaskWorkItemDetail(value: unknown): AgentTaskWorkItemDetailResult {
    const record = asRecord(value);
    const rawQuestions = first(record, 'questions', 'Questions');
    const rawEvents = first(record, 'proposedChangeEvents', 'ProposedChangeEvents');
    const rawAssumptions = first(record, 'assumptions', 'Assumptions');

    return {
        ...(record as unknown as AgentTaskWorkItemDetailResult),
        workItemId: number(first(record, 'workItemId', 'WorkItemId')),
        snapshotId: number(first(record, 'snapshotId', 'SnapshotId')),
        jobName: optionalString(first(record, 'jobName', 'JobName')) ?? '',
        canCreatePR: boolean(first(record, 'canCreatePR', 'CanCreatePR')),
        jobStatus: number(first(record, 'jobStatus', 'JobStatus')),
        pullRequestStatus: number(first(record, 'pullRequestStatus', 'PullRequestStatus')),
        pullRequestHealth: optionalNumber(first(record, 'pullRequestHealth', 'PullRequestHealth')),
        agencyLogsAvailable: first(record, 'agencyLogsAvailable', 'AgencyLogsAvailable') === true,
        agencyLogsArchiveAvailable: first(record, 'agencyLogsArchiveAvailable', 'AgencyLogsArchiveAvailable') === true,
        taskListId: number(first(record, 'taskListId', 'TaskListId')),
        taskId: number(first(record, 'taskId', 'TaskId')),
        taskScope: optionalString(first(record, 'taskScope', 'TaskScope')) ?? '',
        proposedChanges: optionalString(first(record, 'proposedChanges', 'ProposedChanges')),
        codeChanges: optionalString(first(record, 'codeChanges', 'CodeChanges')),
        resultSummary: optionalString(first(record, 'resultSummary', 'ResultSummary')),
        assumptions: (Array.isArray(rawAssumptions) ? rawAssumptions : [])
            .filter((item): item is string => typeof item === 'string' && !!item.trim()),
        noChangeReason: optionalString(first(record, 'noChangeReason', 'NoChangeReason')),
        expectedReductionPercent: optionalNumber(first(record, 'expectedReductionPercent', 'ExpectedReductionPercent')),
        taskSnapshotInclusivePercent: optionalNumber(first(record, 'taskSnapshotInclusivePercent', 'TaskSnapshotInclusivePercent')),
        expectedSnapshotReductionPercent: optionalNumber(first(record, 'expectedSnapshotReductionPercent', 'ExpectedSnapshotReductionPercent')),
        aiu: optionalNumber(first(record, 'aiu', 'Aiu')),
        shutdownStats: optionalString(first(record, 'shutdownStats', 'ShutdownStats')),
        proposedChangeEvents: (Array.isArray(rawEvents) ? rawEvents : []).map(normalizeAgentTaskProposedChange),
        questions: (Array.isArray(rawQuestions) ? rawQuestions : []) as AgentTaskClarificationQuestion[],
    };
}
